import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import ListItem from "@/components/list-item"
import ListInput from "@/components/list-input"
import ListButton from "@/components/list-button"
import TextInput from "@/components/text-input"
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import ColorPickerWidget from "@/widgets/color-picker.widget"
import { SESSION_COLORS } from "@/redux/_session.types"

type Item = PhysicalRiskScaleItem & { color?: string }

type Props<K extends string> = {
  title: string
  scaleKey: K
  items: Item[]
  //Premier niveau sans valeur a saisir (risque residuel : il va de 0 au seuil du niveau suivant)
  isFirstValueFixed?: boolean
  //Couleur de chaque niveau, au choix
  withColor?: boolean
  //Niveaux sans precision
  isDescriptionHidden?: boolean

  onEditItem: (scaleKey: K, index: number, patch: Partial<Item>) => void
  onAddItem: (scaleKey: K, index: number) => void
  onRemoveItem: (scaleKey: K, index: number) => void
}

export default function RiskScaleSection<K extends string>({
  title,
  scaleKey,
  items,
  isFirstValueFixed,
  withColor,
  isDescriptionHidden,
  onEditItem,
  onAddItem,
  onRemoveItem
}: Props<K>) {
  return (
    <ListItem isSmall>
      <div className="flex-col w-full">
        <h3 className="font-bold mb-4">{title}</h3>

        {items.map((item, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-3 mb-2">
            <div className="flex-1 min-w-[240px] flex flex-col gap-2">
              <ListInput value={item.label ?? ""} onEditEnd={(label: string) => onEditItem(scaleKey, i, { label })} />
              {/* Precision facultative, affichee sous le libelle dans les echelles. Grise et a taille
                  constante qu'elle soit renseignee ou non ; l'input d'edition, lui, reste lisible. */}
              {!isDescriptionHidden && (
                <div className="grey-t [&_input]:text-slate-700">
                  <ListInput
                    value={item.description ?? ""}
                    emptyValue={<span className="_hover">{t("Ajouter une précision")}</span>}
                    onEditEnd={(description: string) => onEditItem(scaleKey, i, { description })}
                  />
                </div>
              )}
            </div>

            {withColor && (
              <ColorPickerWidget
                colors={SESSION_COLORS}
                selectedColor={item.color}
                isCustom
                hideLabel
                onSelect={(color: string) => onEditItem(scaleKey, i, { color })}
              />
            )}

            {/* Hauteur du champ de saisie : le texte du premier niveau garde la ligne a la meme hauteur */}
            <div className="w-[150px] min-h-[53px] shrink-0 flex items-center">
              {isFirstValueFixed && i === 0 ? (
                <span className="grey-t">{items[1] ? `< ${items[1].value}` : ""}</span>
              ) : (
                <TextInput
                  type="number"
                  min={0}
                  step="any"
                  value={item.value}
                  width={150}
                  onChange={(event) => onEditItem(scaleKey, i, { value: Number(event.value) })}
                />
              )}
            </div>

            <div className="flex gap-2 justify-end shrink-0">
              {/* Un barème a toujours un premier niveau : il ne se supprime pas */}
              <ListButton
                icon={faTrash}
                isInactive={i === 0}
                text={`${t("utils_delete")} ${title.toLowerCase()}`}
                onClick={() => i > 0 && onRemoveItem(scaleKey, i)}
              />
              <ListButton
                icon={faPlus}
                text={`${t("utils_add")} ${title.toLowerCase()}`}
                onClick={() => onAddItem(scaleKey, i)}
              />
            </div>
          </div>
        ))}
      </div>
    </ListItem>
  )
}
